import { Dispatch, useEffect } from "react";
import produce from "immer";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import * as Sentry from "@sentry/react";
import { Button } from "@material-ui/core";
import apiRequest, {
  getErrorCode,
  getErrorDescription,
  RequestMethod,
} from "utils/apiRequestWithErrorCode";
import {
  isWebhookFolderError,
  isWebhookForbiddenError,
} from "../Form/Steps/Target/StorageWebhook/utils";
import useOrganization from "utils/useOrganization";
import useMailboxAuthPopup from "utils/useMailboxAuthPopup";
import { getUser } from "utils/authentication";
import { isWebhookProcessingUpdate } from "../Form/context/helpers";

import { WebhookCheckResponse } from "@dashboard-v3/api";
import { Rule, RuleUrlParams } from "../common/types";
import { State } from "../Form/context/types";
import { Action } from "../Form/context/action";
import { typePath } from "pages/Rules/Form/context/ruleSelectors";
import { ListTab } from "../List";

const mailboxPermissionsErrors = {
  MAILBOX_REAUTH_REQUIRED: {
    authType: "office365/individual" as const,
    key: "mailboxReauth",
  },
  MAILBOX_RECONSENT_REQUIRED: {
    authType: "office365/organization" as const,
    key: "mailboxReconsent",
  },
};

export default function useSaveRule(state: State, dispatch: Dispatch<Action>) {
  const { t } = useTranslation("rules");
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { open: openMailboxAuth } = useMailboxAuthPopup();
  const { action }: RuleUrlParams = useParams();
  const history = useHistory();
  const { updateOrganization } = useOrganization();
  const isEdit = Boolean(state.rule.id) && action === "edit";
  const isCopy = Boolean(state.rule.id) && action === "copy";

  const handleWebhookFolderError = (error: unknown) => {
    try {
      const details = getErrorDescription(error) as WebhookCheckResponse;
      const webhookStatus: State["webhookStatus"] = {
        isValid: false,
        userWithoutAccess: false,
      };

      if ("foldersInUse" in details) {
        webhookStatus.foldersInUse = details.foldersInUse;
      }

      if ("missingFolder" in details) {
        webhookStatus.missingFolder = details.missingFolder;
      }

      enqueueSnackbar(t("webhookFoldersError"), { variant: "error" });
      dispatch({
        type: "updateStep",
        payload: { step: "target", changes: { webhookStatus } },
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: { rule: state.rule },
        user: getUser(),
      });
      throw error;
    }
  };

  const handleWebhookForbiddenError = () => {
    enqueueSnackbar(t("webhookForbiddenError"), {
      action: () => (
        <Button
          color="inherit"
          target="_blank"
          href="https://support.mxhero.com/hc/en-us/articles/115002669183-Setting-up-a-BOX-Enterprise-or-Business-account"
        >
          {t("helpLinkLabel")}
        </Button>
      ),
      variant: "error",
    });
  };

  // Al mailbox de Office365 le faltan los permisos de Graph que necesita el
  // replace attachments (MXH-5916): la cuenta individual se reautentica, el
  // app de organizacion se reinstala, y el usuario vuelve a guardar.
  const handleMailboxPermissionsError = (
    errorCode: keyof typeof mailboxPermissionsErrors,
    error: unknown
  ) => {
    const { authType, key } = mailboxPermissionsErrors[errorCode];
    // Cada codigo manda lo suyo (email o tenant) y cada texto usa lo que quiere.
    const details = getErrorDescription(error) as Record<string, unknown>;

    enqueueSnackbar(t(`${key}Required`, details), {
      variant: "error",
      persist: true,
      action: snackbarId => (
        <Button
          color="inherit"
          onClick={() => {
            closeSnackbar(snackbarId);
            openMailboxAuth(authType)
              .then(response => {
                if (response?.auth) {
                  enqueueSnackbar(t(`${key}Done`), { variant: "success" });
                }
              })
              .catch(() => undefined);
          }}
        >
          {t(`${key}Action`)}
        </Button>
      ),
    });
  };

  useEffect(() => {
    async function saveRule() {
      try {
        const method: RequestMethod = isEdit ? "PUT" : "POST";
        const path = isEdit ? `/rules/${state.rule.id}` : "/rules";
        const successMsg = isEdit ? t("updateSuccess") : t("createSuccess");

        const body = produce(state.rule, draft => {
          const ruleByPath = typePath(state.type, draft);

          if (isCopy && "mailbox" in ruleByPath) {
            ruleByPath.mailbox.canRestart = true;
          }

          // a continuous rule saved before MXH-6137 can still carry them
          if ("mailbox" in ruleByPath && ruleByPath.mailbox.repeatable) {
            ruleByPath.mailbox.notifications = {
              ...ruleByPath.mailbox.notifications,
              onDone: false,
              onError: false,
            };
          }
        });

        await apiRequest<Rule>(method, path, body);

        if (isWebhookProcessingUpdate(state)) {
          const { currentValue } = state.webhookProcessing;
          await updateOrganization({
            storagesPolicy: { preventReProcessingDragAndDrop: currentValue },
          });
        }

        enqueueSnackbar(successMsg, { variant: "success" });

        const redirectUrl = body.enabled
          ? `/rules/list?tab=${ListTab.Executing}`
          : `/rules/list?tab=${ListTab.All}`;

        history.push(redirectUrl);
      } catch (error) {
        const errorCode = getErrorCode(error) as string;

        Sentry.captureException(error, {
          extra: { rule: state.rule },
          user: getUser(),
        });

        if (isWebhookFolderError(state, error)) handleWebhookFolderError(error);
        else if (isWebhookForbiddenError(error)) handleWebhookForbiddenError();
        else if (errorCode in mailboxPermissionsErrors) {
          handleMailboxPermissionsError(
            errorCode as keyof typeof mailboxPermissionsErrors,
            error
          );
        } else {
          enqueueSnackbar(t(isEdit ? "updateFail" : "createFail"), {
            variant: "error",
          });
        }

        dispatch({
          type: "saveRuleFail",
          payload: { errorCode, errorDescription: getErrorDescription(error) },
        });
      }
    }

    if (state.asyncStatus === "saving") {
      saveRule();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.asyncStatus]);
}
